import { ArrowUpDown, Clock, MessageSquare, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type SortOption = 'updated' | 'created' | 'name';
export type SortDirection = 'asc' | 'desc';

interface SessionSortProps {
  sortBy: SortOption;
  sortDirection: SortDirection;
  onSort: (sortBy: SortOption, direction: SortDirection) => void;
}

export function SessionSort({ sortBy, sortDirection, onSort }: SessionSortProps) {
  const sortOptions = [
    { value: 'updated' as const, label: 'Last Updated', icon: Clock },
    { value: 'created' as const, label: 'Created Date', icon: Calendar },
    { value: 'name' as const, label: 'Name', icon: MessageSquare },
  ];

  const currentOption = sortOptions.find(option => option.value === sortBy);

  const handleSort = (newSortBy: SortOption) => {
    if (newSortBy === sortBy) {
      // Toggle direction if same sort option
      onSort(sortBy, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to desc for new sort option
      onSort(newSortBy, 'desc');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 gap-1">
          <ArrowUpDown className="h-3 w-3" />
          <span className="text-xs">
            {currentOption?.label}
            {sortDirection === 'asc' ? ' ↑' : ' ↓'}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {sortOptions.map((option) => {
          const Icon = option.icon;
          return (
            <DropdownMenuItem
              key={option.value}
              onClick={() => handleSort(option.value)}
              className="flex items-center gap-2"
            >
              <Icon className="h-4 w-4" />
              <span>{option.label}</span>
              {sortBy === option.value && (
                <span className="ml-auto text-xs">
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}